import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, AlertTriangle, Package, Users, FileText, Calendar, Search, ChevronDown, ChevronRight, Filter, Download, Loader2 } from 'lucide-react';
import { withCsv } from '@/lib/downloadFormat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function VendorMismatchReport() {
  const loadingState = useState(true); const loading = loadingState[0]; const setLoading = loadingState[1];
  const statsState = useState(null); const stats = statsState[0]; const setStats = statsState[1];
  const reportState = useState(null); const report = reportState[0]; const setReport = reportState[1];
  const suppliersState = useState([]); const suppliers = suppliersState[0]; const setSuppliers = suppliersState[1];
  
  // Filters
  const supplierFilterState = useState(''); const supplierFilter = supplierFilterState[0]; const setSupplierFilter = supplierFilterState[1];
  const dateFromState = useState(''); const dateFrom = dateFromState[0]; const setDateFrom = dateFromState[1];
  const dateToState = useState(''); const dateTo = dateToState[0]; const setDateTo = dateToState[1];
  const poFilterState = useState(''); const poFilter = poFilterState[0]; const setPoFilter = poFilterState[1];
  
  // Expanded suppliers
  const expandedState = useState({}); const expanded = expandedState[0]; const setExpanded = expandedState[1];

  useEffect(function() {
    fetchAll();
    // fetchAll is a local function stable across renders; run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchAll() {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: 'Bearer ' + token };
    Promise.all([
      axios.get(API + '/grn/mismatch/stats', { headers: headers }),
      axios.get(API + '/grn/mismatch/suppliers', { headers: headers }),
      axios.get(API + '/grn/mismatch/report', { headers: headers })
    ]).then(function(res) {
      setStats(res[0].data);
      setSuppliers(res[1].data);
      setReport(res[2].data);
      // Auto expand first supplier
      if (res[2].data.suppliers && res[2].data.suppliers.length > 0) {
        const e = {}; e[res[2].data.suppliers[0].supplier_id] = true; setExpanded(e);
      }
      setLoading(false);
    }).catch(function(err) {
      toast.error('Failed to fetch mismatch report');
      setLoading(false);
    });
  }

  function applyFilters() {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = {};
    if (supplierFilter) params.supplier_id = supplierFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (poFilter) params.po_number = poFilter;
    
    axios.get(API + '/grn/mismatch/report', { headers: { Authorization: 'Bearer ' + token }, params: params })
      .then(function(res) {
        setReport(res.data);
        setLoading(false);
      })
      .catch(function() {
        toast.error('Failed to fetch filtered report');
        setLoading(false);
      });
  }

  function clearFilters() {
    setSupplierFilter('');
    setDateFrom('');
    setDateTo('');
    setPoFilter('');
    fetchAll();
  }

  function toggleSupplier(supplierId) {
    const e = Object.assign({}, expanded);
    e[supplierId] = !expanded[supplierId];
    setExpanded(e);
  }

  function formatDate(d) {
    if (!d) return 'N/A';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) { return d; }
  }

  const exportingState = useState(false); const exporting = exportingState[0]; const setExporting = exportingState[1];

  function exportExcel() {
    setExporting(true);
    const token = localStorage.getItem('token');
    const payload = {};
    if (supplierFilter) payload.supplier_id = supplierFilter;
    if (dateFrom) payload.date_from = dateFrom;
    if (dateTo) payload.date_to = dateTo;
    if (poFilter) payload.po_number = poFilter;
    
    axios.post(API + '/export/vendor-mismatch', payload, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Vendor_Mismatch_Report_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel report exported');
      setExporting(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to export Excel');
      setExporting(false);
    });
  }

  if (loading && !report) {
    return React.createElement('div', { className: 'flex items-center justify-center h-64' },
      React.createElement(RefreshCw, { className: 'w-6 h-6 animate-spin text-gray-400 mr-2' }),
      React.createElement('span', { className: 'text-gray-600' }, 'Loading mismatch report...')
    );
  }

  const totalMismatches = stats ? stats.total_mismatches : 0;
  const uniqueSuppliers = stats ? stats.unique_suppliers_with_mismatches : 0;

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'vendor-mismatch-report' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary flex items-center gap-3' },
          React.createElement(AlertTriangle, { className: 'w-8 h-8 text-orange-500' }),
          'Vendor Mismatch Report'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'Track vendors sending wrong or alternate products')),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { onClick: exportExcel, variant: 'outline', className: 'gap-2', disabled: exporting, 'data-testid': 'export-excel-btn' },
          exporting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Download, { className: 'w-4 h-4' }), 'Export Excel'),
        React.createElement(Button, { onClick: function() { withCsv(exportExcel); }, variant: 'outline', className: 'gap-2', disabled: exporting, 'data-testid': 'export-btn' },
          React.createElement(Download, { className: 'w-4 h-4' }), 'Export CSV'),
        React.createElement(Button, { onClick: fetchAll, variant: 'outline', size: 'sm', className: 'gap-1', 'data-testid': 'refresh-btn' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }), 'Refresh'))),
    
    // Stats Cards
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
      React.createElement(Card, { className: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement('div', { className: 'p-2 rounded-lg bg-orange-500' },
              React.createElement(AlertTriangle, { className: 'w-5 h-5 text-white' })),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm font-medium text-orange-600' }, 'Total Mismatches'),
              React.createElement('p', { className: 'text-2xl font-bold text-orange-800' }, totalMismatches))))),
      React.createElement(Card, { className: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement('div', { className: 'p-2 rounded-lg bg-purple-500' },
              React.createElement(Users, { className: 'w-5 h-5 text-white' })),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm font-medium text-purple-600' }, 'Vendors with Issues'),
              React.createElement('p', { className: 'text-2xl font-bold text-purple-800' }, uniqueSuppliers))))),
      React.createElement(Card, { className: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center gap-3' },
            React.createElement('div', { className: 'p-2 rounded-lg bg-blue-500' },
              React.createElement(FileText, { className: 'w-5 h-5 text-white' })),
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm font-medium text-blue-600' }, 'Records Shown'),
              React.createElement('p', { className: 'text-2xl font-bold text-blue-800' }, report ? report.total_records : 0)))))),
    
    // Filters Card
    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-3' },
        React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
          React.createElement(Filter, { className: 'w-5 h-5 text-gray-500' }), 'Filters')),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-5 gap-4' },
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-sm text-gray-600' }, 'Supplier'),
            React.createElement(Select, { value: supplierFilter || 'all', onValueChange: function(v) { setSupplierFilter(v === 'all' ? '' : v); } },
              React.createElement(SelectTrigger, { className: 'mt-1' },
                React.createElement(SelectValue, { placeholder: 'All Suppliers' })),
              React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: 'all' }, 'All Suppliers'),
                suppliers.map(function(s) {
                  return React.createElement(SelectItem, { key: s.id, value: s.id }, s.name, ' (', s.mismatch_count, ')');
                })))),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-sm text-gray-600' }, 'Date From'),
            React.createElement(Input, { type: 'date', value: dateFrom, onChange: function(e) { setDateFrom(e.target.value); }, className: 'mt-1' })),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-sm text-gray-600' }, 'Date To'),
            React.createElement(Input, { type: 'date', value: dateTo, onChange: function(e) { setDateTo(e.target.value); }, className: 'mt-1' })),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-sm text-gray-600' }, 'PO Number'),
            React.createElement(Input, { value: poFilter, onChange: function(e) { setPoFilter(e.target.value); }, placeholder: 'Search PO...', className: 'mt-1' })),
          React.createElement('div', { className: 'flex items-end gap-2' },
            React.createElement(Button, { onClick: applyFilters, className: 'flex-1' }, 'Apply'),
            React.createElement(Button, { onClick: clearFilters, variant: 'outline' }, 'Clear'))))),
    
    // Report Data - Grouped by Supplier
    report && report.suppliers && report.suppliers.length > 0 ? React.createElement('div', { className: 'space-y-4' },
      report.suppliers.map(function(supplier) {
        const isExpanded = expanded[supplier.supplier_id];
        return React.createElement(Card, { key: supplier.supplier_id, className: 'border-slate-200 overflow-hidden', 'data-testid': 'supplier-group-' + supplier.supplier_id },
          React.createElement('div', { className: 'flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors', onClick: function() { toggleSupplier(supplier.supplier_id); } },
            React.createElement('div', { className: 'flex items-center gap-3' },
              isExpanded ? React.createElement(ChevronDown, { className: 'w-5 h-5 text-slate-500' }) : React.createElement(ChevronRight, { className: 'w-5 h-5 text-slate-500' }),
              React.createElement(Users, { className: 'w-5 h-5 text-orange-500' }),
              React.createElement('div', null,
                React.createElement('p', { className: 'font-semibold text-slate-800' }, supplier.supplier_name),
                React.createElement('p', { className: 'text-xs text-slate-500' }, supplier.total_cases, ' mismatch case(s)'))),
            React.createElement(Badge, { className: 'bg-orange-100 text-orange-800 border-orange-200' }, supplier.total_cases, ' issues')),
          isExpanded && React.createElement(CardContent, { className: 'p-0' },
            React.createElement('div', { className: 'overflow-x-auto' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', { className: 'bg-gray-100' },
                  React.createElement('tr', null,
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'GRN #'),
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'PO #'),
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Original Product (PO)'),
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Received Product (Actual)'),
                    React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Qty Ordered'),
                    React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Qty Received'),
                    React.createElement('th', { className: 'px-3 py-2 text-center font-semibold' }, 'Diff'),
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Remarks'),
                    React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Date'))),
                React.createElement('tbody', { className: 'divide-y' },
                  supplier.mismatches.map(function(m) {
                    return React.createElement('tr', { key: m.id, className: 'hover:bg-orange-50' },
                      React.createElement('td', { className: 'px-3 py-2 font-mono text-blue-600' }, m.grn_number),
                      React.createElement('td', { className: 'px-3 py-2 font-mono' }, m.po_number),
                      React.createElement('td', { className: 'px-3 py-2' },
                        React.createElement('span', { className: 'px-2 py-1 bg-red-50 text-red-700 rounded text-xs' }, m.original_product_name || 'N/A')),
                      React.createElement('td', { className: 'px-3 py-2' },
                        React.createElement('span', { className: 'px-2 py-1 bg-green-50 text-green-700 rounded text-xs' }, m.received_product_name || 'N/A')),
                      React.createElement('td', { className: 'px-3 py-2 text-center' }, m.quantity_ordered),
                      React.createElement('td', { className: 'px-3 py-2 text-center' }, m.quantity_received),
                      React.createElement('td', { className: 'px-3 py-2 text-center font-semibold ' + (m.quantity_diff !== 0 ? 'text-red-600' : 'text-gray-400') }, m.quantity_diff),
                      React.createElement('td', { className: 'px-3 py-2 text-xs text-gray-600 max-w-xs truncate', title: m.remarks }, m.remarks || '-'),
                      React.createElement('td', { className: 'px-3 py-2 text-xs text-gray-500' }, formatDate(m.date)));
                  }))))));
      })
    ) : React.createElement(Card, { className: 'border-dashed border-2' },
      React.createElement(CardContent, { className: 'py-12 text-center' },
        React.createElement(Package, { className: 'w-12 h-12 mx-auto mb-3 text-gray-300' }),
        React.createElement('p', { className: 'text-gray-500 font-medium' }, 'No Mismatch Records'),
        React.createElement('p', { className: 'text-gray-400 text-sm mt-1' }, 'All vendors are sending correct products as per PO')))
  );
}

export default VendorMismatchReport;
